import {
  mdiBellRing,
  mdiClipboardTextOutline,
  mdiViewDashboard,
  mdiMessageTextOutline,
} from "@mdi/js"
import {
  buildViewNavigation,
  type PageTabWithId,
  type ViewPageSnippets,
  type ViewPageSpec,
} from "@kontextwerk/web-sdk/core/navigation/viewNavigation"
import type { PageNavigationEntry } from "@kontextwerk/web-sdk/core/navigation/types"
import type { OrgaViewFrameContext } from "./context"
import { isSubscriptionRestricted } from "./_shared/_logic/access"
import { getTabs as getOverviewTabs } from "./pages/Overview/navigation"

export type OrgaViewPaths = {
  overview: string
  workOrders: string
  notifications: string
  feedback: string
}

export type OrgaViewNavigationCapabilities = {
  restrictNavigation: boolean
  notificationsNavTitle: string
}

export const buildOrgaViewPaths = (
  pathFor?: ((pageId: string) => string) | null,
): OrgaViewPaths => ({
  overview: pathFor ? pathFor("overview") : "",
  workOrders: pathFor ? pathFor("work-orders") : "",
  notifications: pathFor ? pathFor("notifications") : "",
  feedback: pathFor ? pathFor("feedback") : "",
})

const canViewScheduling = (context: OrgaViewFrameContext) =>
  context.permissions.allows("kehrblatt.scheduling", "view")

export const getWorkOrdersTabs = (context: OrgaViewFrameContext): PageTabWithId[] =>
  canViewScheduling(context)
    ? [
        { id: "overview", title: "Aufträge" },
        { id: "calendar", title: "Terminkalender" },
        { id: "communication", title: "Terminkommunikation" },
      ]
    : []

// Own or all notifications; manage includes view.
const getNotificationsTabs = (context: OrgaViewFrameContext): PageTabWithId[] =>
  context.permissions.allows("core.notifications", "view")
    ? [{ id: "inbox", title: "Mitteilungen" }]
    : []

const mainPageSpecs: Array<
  ViewPageSpec<OrgaViewFrameContext, OrgaViewPaths, OrgaViewNavigationCapabilities>
> = [
  {
    id: "overview",
    title: "Übersicht",
    icon: mdiViewDashboard,
    resolvePath: (paths) => paths.overview,
    canOpen: (capabilities) => !capabilities.restrictNavigation,
    getTabs: getOverviewTabs,
  },
  {
    id: "workOrders",
    title: "Arbeiten",
    icon: mdiClipboardTextOutline,
    resolvePath: (paths) => paths.workOrders,
    canOpen: (capabilities, context) =>
      !capabilities.restrictNavigation && canViewScheduling(context),
    getTabs: getWorkOrdersTabs,
  },
  {
    id: "notifications",
    title: ({ capabilities }) => capabilities.notificationsNavTitle,
    icon: mdiBellRing,
    resolvePath: (paths) => paths.notifications,
    canOpen: (capabilities) => !capabilities.restrictNavigation,
    getTabs: getNotificationsTabs,
  },
]

export const buildOrgaViewNavigation = ({
  context,
  paths,
  capabilities,
  snippets,
}: {
  context: OrgaViewFrameContext
  paths: OrgaViewPaths
  capabilities: OrgaViewNavigationCapabilities
  snippets: ViewPageSnippets
}) => buildViewNavigation({ specs: mainPageSpecs, context, paths, capabilities, snippets })

export const buildFeedbackPage = (
  context: OrgaViewFrameContext,
  path: string,
  content: NonNullable<PageNavigationEntry["content"]>,
): PageNavigationEntry | null => {
  if (!path || isSubscriptionRestricted(context)) return null

  return { id: "feedback", title: "Feedback", path, icon: mdiMessageTextOutline, content }
}
